import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';
import {
  GunBarrelChart,
  type GunBarrelWellPoint,
  type GunBarrelSlotItem,
  type GunBarrelPositionEdit,
  type GunBarrelSelectionInfo,
  type GunBarrelChartHandle,
} from 'components/charts/gun-barrel-chart';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import { useLatest } from 'lib/hooks/use-latest';
import { Button, InlineLoading } from '@carbon/react';
import {
  CheckmarkFilled,
  CheckmarkOutline,
  Draggable,
  Edit,
  Maximize,
  Misuse,
  MisuseOutline,
} from '@carbon/react/icons';
import { formatNumber, format0 } from 'utils/number';
import apiRequest from 'lib/request';
import type { GunBarrelRequest } from './types';
import type { GunBarrelResponse } from 'components/charts/gun-barrel-chart/types';
import { buildGunBarrelRequest } from './gun-barrel-request';
import { activeTcWorkspaceAtom } from 'components/type-curve-workspace/atoms';
import { getTcWorkspaceInventoryVerification } from 'types/type-curve-workspace';
import {
  ltdRefreshTriggerAtom,
  selectedSlotAtom,
  slotOverridesAtom,
  unitVerificationsAtom,
  inventoryVerificationEventMutationAtom,
} from './atoms';
import {
  gunBarrelStateAtoms,
  spacingEditsAtoms,
} from './inventory-surface/atoms';
import { useSurface } from './inventory-surface/surface';
import {
  inventoryUnitVerdictKey,
  useInventoryUnitVerdict,
} from './inventory-surface/use-inventory-unit-verdict';
import {
  INVENTORY_WORKSPACE_TASKS_KEY,
  useInventoryWorkspaceTasks,
} from 'pages/inventory-workspace/use-inventory-workspace-tasks';
import {
  tcSelectedDevAreaAtom,
  tcFilteredUwisAtom,
  tcHighlightedFormationAtom,
  tcInventoryGunBarrelScopeAtom,
  tcInventorySpacingAtom,
  tcUnitSpacingConfigsAtom,
} from './type-curve-filters/atoms';
import type { UnitSpacingConfig } from 'types/api-responses';
import { TcUnitNavigator } from './tc-unit-navigator';
import { SlotNotesPanel, type SlotSaveResult } from './slot-notes-panel';
import { valuationTargetAtom } from 'pages/inventory-workspace/unit-builder/atoms';
import styles from './type-curve-gun-barrel.module.scss';

// The Inventory Workspace's workspace-level verdict task (the header's gate).
const INVENTORY_GATE_TASK_PATH = 'inventory_workspace';

const SPACING_LOWER_LIMIT_FT = 200;
const SPACING_UPPER_LIMIT_FT = 5280;

// Stable empty ref so the per-formation memos don't recompute when there are no
// spacing configs (off an inventory surface / before the page-level fetch lands).
const EMPTY_SPACING_CONFIGS: UnitSpacingConfig[] = [];

const fmtFt = (v: number) => formatNumber(v, 0, 0);

// Inventory Workspace read-only spacing: comma-grouped integer, or a dash when
// the zone hasn't set the value yet.
const spacingText = (value: number | null | undefined) => {
  return value !== null && value !== undefined ? format0(value) : '--';
};

export interface TypeCurveGunBarrelProps {
  /** Type curve id. */
  typeCurveId: number;
  /** Structure-grid collection id from the type-curve header. Null when the
   *  header has no default collection. */
  structureGridCollectionId: number | null;
  /** Default horizontal well spacing (ft) from the type-curve header. */
  horizontalWellSpacingFt: number;
  /** Live Cross-line Setback (ft) from the Unit Builder's spacing controls —
   *  draws the gun barrel's keep-out bands. Inventory/builder only. */
  crossLineSetbackFt?: number | null;
  /** Live Unit Well Spacing (ft) from the Unit Builder's spacing controls —
   *  drives the gun barrel's spacing guide lines. Inventory/builder only;
   *  falls back to the stored zone spacing when absent. */
  wellSpacingFt?: number | null;
  /** Callback to refetch inventory verifications from the server after a
   *  verification event succeeds. */
  refetchVerifications?: () => void;
  /** Callback to notify that a unit-level inventory verdict changed (triggers
   *  summary refetch + potential auto-revert in the tasks panel). */
  onInventoryVerdictChanged?: () => void;
  /**
   * Inventory Workspace only: the workspace is unpromoted, so its per-unit
   * verdicts can still change.
   */
  inventoryEditable?: boolean;
  /**
   * When provided, a "Unit builder" action appears in the controls row. It opens
   * the builder for the selected unit, or — when no unit is selected — for a new
   * empty draft (`null`), mirroring the mock's always-available entry point.
   * Optional: only the Inventory Workspace passes it, so the Type Curve details
   * page is unchanged.
   */
  onEditSlots?: (unit: { id: number; name: string } | null) => void;
  /**
   * Drop the unit stepper. The Unit Builder's own title bar already shows the
   * unit with prev/next, so the mock hides this one inside the builder
   * (`{!editSlotsMode && …}`).
   */
  hideUnitNavigator?: boolean;
  /**
   * When provided, an expand (Maximize) icon appears in the controls row; the
   * Inventory Management panel wires it to open the gun barrel + slot table in a
   * modal. Absent (Type Curve page / already-expanded view) hides the icon.
   */
  onExpand?: () => void;
  /**
   * Drop the Unit builder button. The expanded (modal) view keeps the action bar
   * but not this button, so builder edits stay on the inline panel.
   */
  hideUnitBuilderButton?: boolean;
  /**
   * Reports the scoped unit's perpendicular extent as the gun barrel query
   * resolves it. The Unit Builder's slots table needs it to say whether a row
   * reaches into the unit, and only this component fetches it.
   */
  onUnitBoundariesChange?: (
    boundaries: { leftXFt: number; rightXFt: number } | null
  ) => void;
  /**
   * Reports the chart's pending position edits. Kept chart-local otherwise; the
   * Unit Builder needs them to know which slots "Update Geometries" re-cuts.
   */
  onPositionEditsChange?: (
    edits: ReadonlyMap<string, GunBarrelPositionEdit>
  ) => void;
  /**
   * Render the chart alone, without this component's header and controls rows.
   * The Unit Builder supplies its own spacing inputs and action rail, so both
   * rows would appear twice.
   */
  chartOnly?: boolean;
  /**
   * Scoped slots, shared with whoever else can scope them (the Unit Builder's
   * slots table). Passed straight through to the chart.
   */
  selectedSlotKeys?: ReadonlySet<string>;
  onSelectedSlotKeysChange?: (keys: ReadonlySet<string>) => void;
  /** Draw slot / well labels on the chart. */
  showSlotLabels?: boolean;
  /**
   * Publishes the chart selection's live position + count. The Unit Builder's
   * edit rail shows it and gates its X/Y nudge controls on it.
   */
  onSelectionInfoChange?: (info: GunBarrelSelectionInfo) => void;
  /**
   * Forwards the chart's imperative handle up, so the Unit Builder's edit rail
   * can nudge / set / reset the selected element's position off-chart.
   */
  chartHandleRef?: Ref<GunBarrelChartHandle>;
  /**
   * Canvas height, forwarded to the chart. Omitted everywhere but the Unit
   * Builder, which passes a CSS `var()` so the canvas follows its row resizer.
   */
  chartHeight?: number | string;
  /**
   * Overrides which formation the chart emphasises + gates editing to, without
   * changing the fetch. The Unit Builder's formation arrows step through the gun
   * barrel's formations (below) and set this; the request keeps the leaf's
   * formation so stepping never refetches. Falls back to the atom when omitted.
   */
  highlightedFormationOverride?: string | null;
  /**
   * Publishes the gun barrel's formations, ordered shallow→deep, so the Unit
   * Builder's arrows can step through them. Names match the chart's highlight /
   * slot-formation keys.
   */
  onFormationsChange?: (formations: string[]) => void;
  /**
   * Publishes the landing edits the drawn slots are still waiting on (Story 1),
   * so the Unit Builder can say so above the chart and follow them to the
   * refetch that releases them. Empty once every landing has reported.
   */
  onPendingLandingsChange?: (editIds: number[]) => void;
  /** Let other formations' LTDs be clicked into the selection (Merge Geometries). */
  crossFormationPicking?: boolean;
}

// Type-curve workspace Gun Barrel panel: owns the API fetch, Target Spacing
// controls, action buttons, and delegates chart rendering to GunBarrelChart.
export const TypeCurveGunBarrel = ({
  typeCurveId,
  structureGridCollectionId,
  horizontalWellSpacingFt,
  crossLineSetbackFt,
  wellSpacingFt,
  refetchVerifications,
  inventoryEditable = false,
  onEditSlots,
  hideUnitNavigator = false,
  onExpand,
  hideUnitBuilderButton = false,
  onUnitBoundariesChange,
  onPositionEditsChange,
  chartOnly = false,
  selectedSlotKeys,
  onSelectedSlotKeysChange,
  showSlotLabels = false,
  onSelectionInfoChange,
  chartHandleRef,
  chartHeight,
  highlightedFormationOverride,
  onFormationsChange,
  onPendingLandingsChange,
  onInventoryVerdictChanged,
  crossFormationPicking = false,
}: TypeCurveGunBarrelProps) => {
  // This component renders on both inventory surfaces (main + Unit Builder) and on
  // the plain Type Curve page. It reads its surface's gun barrel state; the
  // inventory fetch is owned per surface by `useInventoryGunBarrelFor`, while the
  // plain Type Curve path (surface 'main', inventoryScope null) fetches below.
  const store = useStore();
  const surface = useSurface();
  const gbAtom = gunBarrelStateAtoms[surface];
  const gbState = useAtomValue(gbAtom);
  const plainTcAbortRef = useRef<AbortController | null>(null);
  const activeTcWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  // Bumped whenever slots are (re)generated — e.g. from the Inventory Workspace's
  // Compute Inventory. Re-fires the gun barrel query so the chart reflects the new
  // slots, matching the map + slot table.
  const ltdRefreshTrigger = useAtomValue(ltdRefreshTriggerAtom);
  const setSelectedSlot = useSetAtom(selectedSlotAtom);
  const setValuationTarget = useSetAtom(valuationTargetAtom);
  const [unitVerifications, setUnitVerifications] = useAtom(
    unitVerificationsAtom
  );
  const [verificationEventQuery, runVerificationEvent] = useAtom(
    inventoryVerificationEventMutationAtom
  );
  const verificationLoading = verificationEventQuery.loading;
  const verificationActionRef = useRef<'correct' | 'incorrect' | null>(null);

  // Controls + action buttons are interactive only inside an active
  // (not-yet-promoted) Type Curve Workspace; locked on the plain detail view.
  const workspaceActive = !!activeTcWorkspace && !activeTcWorkspace.promoted;

  const filteredUwis = useAtomValue(tcFilteredUwisAtom);
  // Inventory Workspace only: the selected leaf's formation, highlighted in the
  // chart. Null on the plain Type Curve page.
  const highlightedFormation = useAtomValue(tcHighlightedFormationAtom);
  // Inventory Workspace only: routes the gun barrel to the inventory_unit_slots
  // backend layer. Null on the plain Type Curve page.
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  // Inventory Workspace only: the selected zone's read-only spacing values,
  // shown in the controls in place of the editable Target Spacing input.
  const inventorySpacing = useAtomValue(tcInventorySpacingAtom);
  const inventoryWellSpacingFt = inventorySpacing?.wellSpacingFt ?? null;

  // Per-formation spacing configs for the scoped unit (inventory surfaces only).
  // Fetched here — the one component both the main page and the Unit Builder
  // mount — and reduced to one config per visible formation (the selected/default
  // package per zone), keyed by formation name for the chart's per-band drainage
  // + setback and the selected-formation readout.
  // Read the shared configs — the fetch is owned once at the inventory-workspace
  // page level (so it isn't re-run as the Unit Builder mounts/unmounts this gun
  // barrel). `null` on the plain Type Curve page → empty → scalar fallback.
  const spacingConfigs =
    useAtomValue(tcUnitSpacingConfigsAtom) ?? EMPTY_SPACING_CONFIGS;
  // This surface's live spacing edits (keyed by package id) overlay the stored
  // configs. Only the Unit Builder writes its own (`spacingEditsAtoms.builder`),
  // so the builder chart reflects unsaved edits while the main page
  // (`spacingEditsAtoms.main`, never written) shows the persisted configs — a
  // builder edit no longer leaks onto the main readouts.
  const spacingEdits = useAtomValue(spacingEditsAtoms[surface]);
  const formationSpacingConfigs = useMemo(() => {
    const ids = inventoryScope?.inventoryFormationPackageIds;
    if (!ids || spacingConfigs.length === 0) return [];
    return spacingConfigs
      .filter((c) => ids.includes(c.inventoryFormationPackageId))
      .map((c) => {
        const edit = spacingEdits[c.inventoryFormationPackageId];
        return {
          formation: c.formation,
          wellSpacingFt: edit ? edit.wellSpacingFt : c.wellSpacingFt,
          crossLineSetbackFt: edit
            ? edit.crossLineSetbackFt
            : c.crossLineSetbackFt,
          drainageSectorCount: c.drainageSectorCount,
        };
      });
  }, [spacingConfigs, inventoryScope, spacingEdits]);

  // Per-unit verdict buttons are deliberately NOT gated on overlap resolution, only on the
  // workspace being active. Overlap resolution is a workspace-level decision about which of
  // several competing units to keep; this verdict is a per-unit judgement about whether that
  // unit's own slot layout is right. A reviewer can confirm one while the other is still open.
  // Matches InsightsTypeCurveUnit#guard_verification!, which no longer blocks on overlaps either.
  // Generation above keeps the original gate.
  const verdictEnabled = workspaceActive;

  // When a workspace-level inventory verdict exists, per-unit verification
  // buttons should be disabled — the overall inventory has been signed off.
  const workspaceInventoryVerdict =
    getTcWorkspaceInventoryVerification(activeTcWorkspace ?? {})?.status ??
    null;

  // Unit is considered selected when a dev area is picked on the map.
  const unitSelected = !!selectedDevArea;

  // Single live spacing value driving both the input and the chart, so typing
  // takes effect immediately. Blur/Enter clamps to [lower limit, unit width]
  // and notifies the parent; re-syncs when the source prop changes.
  const [localSpacing, setLocalSpacing] = useState<number | null>(
    horizontalWellSpacingFt
  );
  useEffect(() => {
    setLocalSpacing(horizontalWellSpacingFt);
  }, [horizontalWellSpacingFt]);

  // ── Build & fire request ──────────────────────────────────────────────────
  const request: GunBarrelRequest | null = useMemo(
    () =>
      buildGunBarrelRequest({
        selectedDevArea,
        inventoryScope,
        structureGridCollectionId,
        typeCurveId,
        horizontalWellSpacingFt,
        highlightedFormation,
      }),
    [
      selectedDevArea,
      structureGridCollectionId,
      horizontalWellSpacingFt,
      typeCurveId,
      inventoryScope,
      highlightedFormation,
    ]
  );

  const requestKey = request ? JSON.stringify(request) : null;
  useEffect(() => {
    // The inventory `/gun_barrel` fetch is owned once per surface at the page /
    // modal (`useInventoryGunBarrelFor`) so this component's copy isn't re-POSTed
    // as the Unit Builder mounts/unmounts it — here it just reads the result. The
    // plain Type Curve page (inventoryScope null) fetches into its own 'main'
    // surface atom below. Re-runs on `ltdRefreshTrigger` so a slot (re)generation
    // refreshes the chart without needing the selection to change.
    if (!request || inventoryScope || requestKey === null) return;
    if (plainTcAbortRef.current) plainTcAbortRef.current.abort();
    const controller = new AbortController();
    plainTcAbortRef.current = controller;
    store.set(gbAtom, (prev) => ({
      ...prev,
      loading: true,
      error: null,
      signature: requestKey,
    }));
    void (async () => {
      try {
        const response = await apiRequest.post<GunBarrelResponse, unknown>(
          '/queries/gun_barrel',
          request,
          controller
        );
        if (controller.signal.aborted || response.type === 'abort') return;
        if (response.type === 'success') {
          store.set(gbAtom, {
            loading: false,
            data: response.data,
            error: null,
            signature: requestKey,
          });
          return;
        }
        store.set(gbAtom, {
          loading: false,
          data: null,
          error: 'Failed to load Gun Barrel',
          signature: requestKey,
        });
      } catch {
        if (!controller.signal.aborted) {
          store.set(gbAtom, {
            loading: false,
            data: null,
            error: 'Failed to load Gun Barrel',
            signature: requestKey,
          });
        }
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, ltdRefreshTrigger, inventoryScope, store, gbAtom]);

  const gunBarrelLoading = gbState.loading;
  const gunBarrelData = gbState.data ?? undefined;
  const gunBarrelError = gbState.error;

  // ── Spacing input ─────────────────────────────────────────────────────────
  // Valid spacing must sit within [200, 5,280] ft. Out-of-range values are kept
  // in the field (so the user sees what they typed) but flagged with an error
  // and excluded from the chart.
  const spacingInRange =
    localSpacing !== null &&
    localSpacing >= SPACING_LOWER_LIMIT_FT &&
    localSpacing <= SPACING_UPPER_LIMIT_FT;
  const spacingError = localSpacing !== null && !spacingInRange;

  const wellsPerMile = spacingInRange
    ? Math.floor(5280 / (localSpacing as number))
    : null;

  const handleSpacingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setLocalSpacing(raw === '' ? null : Number(raw));
  };

  const commitSpacing = useCallback(
    (raw: number) => {
      if (Number.isNaN(raw)) {
        setLocalSpacing(horizontalWellSpacingFt);
        return;
      }
      setLocalSpacing(raw);
    },
    [horizontalWellSpacingFt]
  );

  // ── Inventory verification ────────────────────────────────────────────────
  const currentVerification = selectedDevArea
    ? (unitVerifications.get(selectedDevArea.developmentAreaName) ?? null)
    : null;
  const inventoryStatus =
    currentVerification?.inventoryVerification.status ?? null;
  const verificationItemId =
    currentVerification?.inventoryVerification.id ?? null;
  const handleMarkCorrect = useCallback(() => {
    if (!verificationItemId || !currentVerification || !selectedDevArea)
      return console.log(
        'no verification item id or current verification or selected dev area'
      );

    const isReverting = inventoryStatus === true;
    const kind = isReverting ? 'verification_reverted' : 'verification_correct';
    const newStatus = isReverting ? null : true;

    verificationActionRef.current = 'correct';
    refetchVerifications?.();
    runVerificationEvent({
      url: `/verification_items/${verificationItemId}/events`,
      type: 'POST',
      data: { kind, reason: null, details: null },
      onSuccess: () => {
        verificationActionRef.current = null;
        refetchVerifications?.();
        onInventoryVerdictChanged?.();
      },
      onError: () => {
        verificationActionRef.current = null;
      },
    });

    // Optimistic update
    setUnitVerifications((prev) => {
      const next = new Map(prev);
      const entry = next.get(selectedDevArea.developmentAreaName);
      if (entry) {
        next.set(selectedDevArea.developmentAreaName, {
          ...entry,
          inventoryVerification: {
            ...entry.inventoryVerification,
            status: newStatus,
          },
        });
      }
      return next;
    });
  }, [
    verificationItemId,
    currentVerification,
    selectedDevArea,
    inventoryStatus,
    runVerificationEvent,
    setUnitVerifications,
    refetchVerifications,
    onInventoryVerdictChanged,
  ]);

  const handleMarkIncorrect = useCallback(() => {
    if (!verificationItemId || !currentVerification || !selectedDevArea) return;

    const isReverting = inventoryStatus === false;
    const kind = isReverting
      ? 'verification_reverted'
      : 'verification_incorrect';
    const newStatus = isReverting ? null : false;

    verificationActionRef.current = 'incorrect';
    runVerificationEvent({
      url: `/verification_items/${verificationItemId}/events`,
      type: 'POST',
      data: { kind, reason: null, details: null },
      onSuccess: () => {
        verificationActionRef.current = null;
        refetchVerifications?.();
        onInventoryVerdictChanged?.();
      },
      onError: () => {
        verificationActionRef.current = null;
      },
    });

    // Optimistic update
    setUnitVerifications((prev) => {
      const next = new Map(prev);
      const entry = next.get(selectedDevArea.developmentAreaName);
      if (entry) {
        next.set(selectedDevArea.developmentAreaName, {
          ...entry,
          inventoryVerification: {
            ...entry.inventoryVerification,
            status: newStatus,
          },
        });
      }
      return next;
    });
  }, [
    verificationItemId,
    currentVerification,
    selectedDevArea,
    inventoryStatus,
    runVerificationEvent,
    setUnitVerifications,
    refetchVerifications,
    onInventoryVerdictChanged,
  ]);

  // ── Inventory Workspace verification ──────────────────────────────────────
  // The same per-unit verdict flow as above, on the Inventory Workspace's own
  // unit membership. The Type Curve data above is never read or written here:
  // on this page it falls back to the latest promoted Type Curve Workspace.
  const queryClient = useQueryClient();
  const inventoryWorkspaceId = inventoryScope?.inventoryWorkspaceId ?? null;
  const inventoryUnitId = inventoryScope
    ? (selectedDevArea?.developmentAreaId ?? null)
    : null;
  const { verdict: inventoryVerdict } = useInventoryUnitVerdict(
    inventoryWorkspaceId,
    inventoryUnitId,
    selectedDevArea?.developmentAreaName ?? null
  );
  // Same query key as the page's checklist, so this is a cache read.
  const inventoryTasks = useInventoryWorkspaceTasks(
    inventoryWorkspaceId ?? undefined
  );
  // Once the workspace-level verdict is set, per-unit verdicts are locked, as
  // they are on the Type Curve Workspace.
  const inventoryGateStatus =
    inventoryTasks.data?.tasks.find(
      (task) => task.path === INVENTORY_GATE_TASK_PATH
    )?.status ?? null;

  const handleInventoryVerdict = useCallback(
    (target: boolean) => {
      const itemId = inventoryVerdict.verificationId;
      if (itemId === null || inventoryUnitId === null) return;
      const isReverting = inventoryVerdict.status === target;
      const kind = isReverting
        ? 'verification_reverted'
        : target
          ? 'verification_correct'
          : 'verification_incorrect';
      const key = inventoryUnitVerdictKey(
        inventoryWorkspaceId,
        inventoryUnitId
      );

      verificationActionRef.current = target ? 'correct' : 'incorrect';
      runVerificationEvent({
        url: `/verification_items/${itemId}/events`,
        type: 'POST',
        data: { kind, reason: null, details: null },
        onSuccess: () => {
          verificationActionRef.current = null;
          void queryClient.invalidateQueries({ queryKey: key });
          void queryClient.invalidateQueries({
            queryKey: [INVENTORY_WORKSPACE_TASKS_KEY, inventoryWorkspaceId],
          });
        },
        onError: () => {
          verificationActionRef.current = null;
          void queryClient.invalidateQueries({ queryKey: key });
        },
      });

      // Optimistic update
      queryClient.setQueryData(key, {
        verificationId: itemId,
        status: isReverting ? null : target,
      });
    },
    [
      inventoryVerdict,
      inventoryUnitId,
      inventoryWorkspaceId,
      runVerificationEvent,
      queryClient,
    ]
  );

  // Extract wells and slots from the gun-barrel response so the chart receives
  // flat arrays instead of the full response envelope.
  const wells = useMemo<GunBarrelWellPoint[]>(() => {
    const layer = gunBarrelData?.poiLayers?.find((l) => l.kind === 'wells');
    return layer && 'wells' in layer ? layer.wells : [];
  }, [gunBarrelData]);

  // Publish the unit extent for consumers that cannot fetch it themselves.
  useEffect(() => {
    onUnitBoundariesChange?.(gunBarrelData?.subject?.unitBoundaries ?? null);
  }, [gunBarrelData, onUnitBoundariesChange]);

  // The gun barrel's formations, shallow→deep, for the Unit Builder's arrows to
  // step through. These are the highlightable bands whose names match both the
  // chart highlight and the slots' formation, so stepping drives both.
  const gunBarrelFormations = useMemo<string[]>(() => {
    const tcs = gunBarrelData?.formationHorizontals?.typeCurveFormations ?? [];
    const avgTop = (grid: (number | null)[][] | null | undefined): number => {
      const row = grid?.[0] ?? [];
      const vals = row.filter(
        (v): v is number => v !== null && v !== undefined
      );
      return vals.length
        ? vals.reduce((a, b) => a + b, 0) / vals.length
        : Number.NEGATIVE_INFINITY;
    };
    return (
      [...tcs]
        // Shallower first: TVDSS is negative, so the higher (less-negative) top wins.
        .sort((a, b) => avgTop(b.topTvdss) - avgTop(a.topTvdss))
        .map((tc) => tc.name)
        .filter((name): name is string => !!name)
    );
  }, [gunBarrelData]);

  const onFormationsChangeRef = useRef(onFormationsChange);
  onFormationsChangeRef.current = onFormationsChange;
  useEffect(() => {
    onFormationsChangeRef.current?.(gunBarrelFormations);
  }, [gunBarrelFormations]);

  // Chart highlight + edit gating follow the stepped formation when supplied,
  // while the fetch keeps the atom's formation so stepping never refetches.
  const chartHighlightedFormation =
    highlightedFormationOverride ?? highlightedFormation;

  // The selected formation's resolved config (within the visible/default package
  // set), for the read-only spacing values above the chart — so they follow the
  // formation the user is on and update as it changes.
  const selectedFormationConfig = useMemo(() => {
    const fmt = chartHighlightedFormation?.toUpperCase();
    if (!fmt) return null;
    // The selected package first: the visible set holds one package per
    // (area, formation), so a formation name shared by two areas would
    // otherwise match whichever comes first.
    const selectedId = inventoryScope?.inventoryFormationPackageId;
    const selected = spacingConfigs.find(
      (c) =>
        c.inventoryFormationPackageId === selectedId &&
        c.formation.toUpperCase() === fmt
    );
    if (selected) return selected;
    const ids = inventoryScope?.inventoryFormationPackageIds;
    return (
      spacingConfigs.find(
        (c) =>
          c.formation.toUpperCase() === fmt &&
          (!ids || ids.includes(c.inventoryFormationPackageId))
      ) ?? null
    );
  }, [spacingConfigs, chartHighlightedFormation, inventoryScope]);

  const serverSlots = useMemo<GunBarrelSlotItem[]>(() => {
    // Inventory Workspace: the response carries every configured zone. Show ALL
    // zones' slots (every formation) — the chart places each into its own band by
    // xOffset/tvdss/color; the selected formation is highlighted separately via
    // `highlightedFormation`. Switching formations re-highlights without a refetch.
    if (inventoryScope) {
      const layer = gunBarrelData?.poiLayers?.find(
        (l: { kind: string }) => l.kind === 'inventory_unit_slots'
      );
      if (!layer || !('zones' in layer)) return [];
      // The chart keys slots by a numeric `slotId`; the inventory layer's
      // `slotId` is a string, so use the numeric row `id` (unique across zones).
      // Tag each slot with its zone's formation so the chart can gate editing to
      // the highlighted formation (only that formation's slots drag/nudge).
      return layer.zones.flatMap((zone) =>
        zone.slots.map((s) => ({
          ...s,
          slotId: s.id,
          formationLabel: zone.formationName,
          // Story 1: whether a permit / pre-drill slot may be moved in depth,
          // and whether a move of it is still landing.
          landingEditable: s.landingEditable === true,
          landingPending: s.landingPending === true,
          pendingLandingEditId: s.pendingLandingEditId ?? null,
        }))
      );
    }
    const layer = gunBarrelData?.poiLayers?.find(
      (l: { kind: string }) => l.kind === 'insights_unit_formation_slots'
    );
    return layer && 'slots' in layer
      ? (layer.slots as GunBarrelSlotItem[])
      : [];
  }, [gunBarrelData, inventoryScope]);

  // The edits the drawn slots are waiting on, one per held well. Keyed by the
  // joined ids so a refetch that changes nothing publishes nothing.
  const pendingLandingEditIds = useMemo(() => {
    const ids = serverSlots
      .map((s) => s.pendingLandingEditId)
      .filter((id): id is number => typeof id === 'number');
    return [...new Set(ids)].sort((a, b) => a - b);
  }, [serverSlots]);
  const pendingLandingKey = pendingLandingEditIds.join(',');
  const onPendingLandingsChangeRef = useLatest(onPendingLandingsChange);
  useEffect(() => {
    onPendingLandingsChangeRef.current?.(pendingLandingEditIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pendingLandingKey stands in for the array identity
  }, [pendingLandingKey, onPendingLandingsChangeRef]);

  // Selection-key bridge (Inventory Workspace only). The slots table keys its
  // shared selection by the inventory layer's STRING slotId (`{unit}_{fmt}_{n}`),
  // but the chart keys by the numeric row `id` (the flatten above). Map between
  // them so ticking a table row and clicking a chart slot select the same thing.
  const slotIdBridge = useMemo(() => {
    const strToNum = new Map<string, number>();
    const numToStr = new Map<number, string>();
    if (!inventoryScope) return { strToNum, numToStr };
    const layer = gunBarrelData?.poiLayers?.find(
      (l: { kind: string }) => l.kind === 'inventory_unit_slots'
    );
    if (layer && 'zones' in layer) {
      layer.zones.forEach((zone) =>
        zone.slots.forEach((s) => {
          strToNum.set(s.slotId, s.id);
          numToStr.set(s.id, s.slotId);
        })
      );
    }
    return { strToNum, numToStr };
  }, [gunBarrelData, inventoryScope]);

  // Translate the shared (string-keyed) selection into the chart's numeric keys.
  const chartSelectedKeys = useMemo<ReadonlySet<string> | undefined>(() => {
    if (!inventoryScope || !selectedSlotKeys) return selectedSlotKeys;
    const out = new Set<string>();
    selectedSlotKeys.forEach((key) => {
      if (key.startsWith('slot:')) {
        const num = slotIdBridge.strToNum.get(key.slice('slot:'.length));
        if (num !== undefined) out.add(`slot:${num}`);
      } else {
        out.add(key); // `uwi:` keys are shared verbatim.
      }
    });
    return out;
  }, [inventoryScope, selectedSlotKeys, slotIdBridge]);

  // Translate the chart's numeric-keyed selection back to the shared string keys.
  const handleChartSelectedKeys = useCallback(
    (keys: ReadonlySet<string>) => {
      if (!inventoryScope) {
        onSelectedSlotKeysChange?.(keys);
        return;
      }
      const out = new Set<string>();
      keys.forEach((key) => {
        if (key.startsWith('slot:')) {
          const str = slotIdBridge.numToStr.get(
            Number(key.slice('slot:'.length))
          );
          if (str !== undefined) out.add(`slot:${str}`);
        } else {
          out.add(key);
        }
      });
      onSelectedSlotKeysChange?.(out);
    },
    [inventoryScope, slotIdBridge, onSelectedSlotKeysChange]
  );

  // Shared overrides for slot valued/notes — applied on top of the server data
  // so the chart + map update immediately without a re-query. Reset when the
  // server data changes (new dev area or page refresh).
  const [slotOverrides, setSlotOverrides] = useAtom(slotOverridesAtom);

  // Reset overrides when the server data changes
  useEffect(() => {
    setSlotOverrides(new Map());
  }, [serverSlots, setSlotOverrides]);

  const gbSlots = useMemo<GunBarrelSlotItem[]>(() => {
    if (slotOverrides.size === 0) return serverSlots;
    return serverSlots.map((s) => {
      const override = slotOverrides.get(s.slotId);
      return override ? { ...s, ...override } : s;
    });
  }, [serverSlots, slotOverrides]);

  // ── Slot notes panel ────────────────────────────────────────────────────
  // Click = immediate optimistic toggle of `valued` + open panel.
  // Save persists the already-toggled state to the backend.
  // Cancel/Close reverts the optimistic toggle.
  const handleSlotClick = useCallback(
    (slotId: number) => {
      const slot = gbSlots.find((s) => s.slotId === slotId) ?? null;
      if (!slot) return;

      // Toggle valued immediately so the cross icon appears/disappears on click
      const newValued = !slot.valued;

      // If toggling returns the slot to its original server state, revert the
      // override and close the panel instead of re-opening it.
      const serverSlot = serverSlots.find((s) => s.slotId === slotId);
      if (serverSlot && newValued === serverSlot.valued) {
        setSlotOverrides((prev) => {
          const next = new Map(prev);
          next.delete(slotId);
          return next;
        });
        setSelectedSlot(null);
        return;
      }

      setSlotOverrides((prev) => {
        const next = new Map(prev);
        next.set(slotId, { valued: newValued, notes: slot.notes });
        return next;
      });

      // Open the panel with the toggled slot state
      setSelectedSlot({ ...slot, valued: newValued });
    },
    [gbSlots, serverSlots, setSelectedSlot, setSlotOverrides]
  );

  const handleSlotSaved = useCallback(
    (result: SlotSaveResult) => {
      // Override already applied on click — update notes from the panel
      setSlotOverrides((prev) => {
        const next = new Map(prev);
        next.set(result.slotId, { valued: result.valued, notes: result.notes });
        return next;
      });
    },
    [setSlotOverrides]
  );

  const handleSlotCancelled = useCallback(
    (slotId: number, originalValued: boolean, originalNotes: string | null) => {
      // Revert the optimistic toggle
      setSlotOverrides((prev) => {
        const next = new Map(prev);
        next.set(slotId, { valued: originalValued, notes: originalNotes });
        return next;
      });
    },
    [setSlotOverrides]
  );

  // Unit Builder (inventory) valuation: right-click ANY slot marker (any well
  // type) to value/unvalue it. Mirrors the click flow's optimistic toggle, but
  // opens the Notes UI in the edit rail (via `valuationTargetAtom`) instead of
  // the floating panel, and is front-end only (no PATCH) for now.
  const handleSlotRightClick = useCallback(
    (slotId: number) => {
      const slot = gbSlots.find((s) => s.slotId === slotId) ?? null;
      const serverSlot = serverSlots.find((s) => s.slotId === slotId);
      if (!slot || !serverSlot) return;

      const newValued = !slot.valued;

      // Toggling back to the server value clears the override and closes the
      // Notes section (re-valuing needs no note).
      if (newValued === serverSlot.valued) {
        setSlotOverrides((prev) => {
          const next = new Map(prev);
          next.delete(slotId);
          return next;
        });
        setValuationTarget(null);
        return;
      }

      setSlotOverrides((prev) => {
        const next = new Map(prev);
        next.set(slotId, { valued: newValued, notes: slot.notes });
        return next;
      });

      const label =
        slotIdBridge.numToStr.get(slotId) ??
        slot.uwi ??
        `${slot.formationLabel ?? 'Property'} ${slot.slotNumber}`;
      setValuationTarget({
        slotId,
        label,
        valued: newValued,
        notes: slot.notes,
        originalValued: serverSlot.valued,
        originalNotes: serverSlot.notes,
      });
    },
    [gbSlots, serverSlots, slotIdBridge, setSlotOverrides, setValuationTarget]
  );

  // Gun barrel edit mode: drag slots/wells inside the unit boundary to new
  // positions. Edits stay chart-local (pending) — no persistence flow yet.
  const [gbEditMode, setGbEditMode] = useState(false);
  const [gbPositionEdits, setGbPositionEdits] = useState<
    ReadonlyMap<string, GunBarrelPositionEdit>
  >(() => new Map());

  // Close slot panel and leave edit mode when the dev area changes
  useEffect(() => {
    setSelectedSlot(null);
    setGbEditMode(false);
  }, [selectedDevArea, setSelectedSlot]);

  // The verdict + Unit builder controls are shared by both the Type Curve
  // two-row layout and the Inventory single wrapping row, so build them once.
  // An Inventory Workspace reads and writes its own unit verdicts.
  const shownStatus = inventoryScope
    ? inventoryVerdict.status
    : inventoryStatus;
  const verdictDisabled = inventoryScope
    ? !inventoryEditable ||
      inventoryVerdict.verificationId === null ||
      verificationLoading ||
      inventoryGateStatus !== null
    : !verdictEnabled ||
      !verificationItemId ||
      verificationLoading ||
      !!workspaceInventoryVerdict;
  const verdictButtons = (
    <>
      <Button
        kind={shownStatus === true ? 'primary' : 'tertiary'}
        size="sm"
        hasIconOnly
        iconDescription="Mark correct"
        tooltipPosition="bottom"
        renderIcon={
          verificationLoading && verificationActionRef.current === 'correct'
            ? () => <InlineLoading description="" />
            : shownStatus === true
              ? CheckmarkFilled
              : CheckmarkOutline
        }
        disabled={verdictDisabled}
        onClick={
          inventoryScope
            ? () => handleInventoryVerdict(true)
            : handleMarkCorrect
        }
      />
      <Button
        kind={shownStatus === false ? 'primary' : 'tertiary'}
        size="sm"
        hasIconOnly
        iconDescription="Mark incorrect"
        tooltipPosition="bottom"
        renderIcon={
          verificationLoading && verificationActionRef.current === 'incorrect'
            ? () => <InlineLoading description="" />
            : shownStatus === false
              ? Misuse
              : MisuseOutline
        }
        disabled={verdictDisabled}
        onClick={
          inventoryScope
            ? () => handleInventoryVerdict(false)
            : handleMarkIncorrect
        }
      />
    </>
  );

  const unitBuilderButton =
    onEditSlots && !hideUnitBuilderButton ? (
      <Button
        kind="primary"
        size="sm"
        renderIcon={Edit}
        // Always available (mock parity): with a unit selected it edits that unit;
        // with none, it opens an empty draft to build a new unit from scratch.
        onClick={() =>
          onEditSlots(
            selectedDevArea
              ? {
                  id: selectedDevArea.developmentAreaId,
                  name: selectedDevArea.developmentAreaName,
                }
              : null
          )
        }
      >
        Unit builder
      </Button>
    ) : null;

  const expandButton = onExpand ? (
    <Button
      kind="ghost"
      size="sm"
      hasIconOnly
      iconDescription="Expand"
      tooltipPosition="left"
      renderIcon={Maximize}
      onClick={onExpand}
    />
  ) : null;

  // Inventory Workspace: the selected formation's read-only spacing values, shown
  // in place of the editable Target Spacing input. Prefer the per-formation config
  // (from the spacing_configs API, following the selected formation); fall back to
  // the zone-details spacing before it loads.
  // A live builder edit for the selected formation's package wins over the stored
  // config (so the readout tracks what the user just typed in the builder).
  const selectedFormationEdit = selectedFormationConfig
    ? spacingEdits[selectedFormationConfig.inventoryFormationPackageId]
    : undefined;
  const readoutSpacing =
    selectedFormationEdit ?? selectedFormationConfig ?? inventorySpacing;
  const readoutWellsPerMile =
    readoutSpacing?.wellSpacingFt && readoutSpacing.wellSpacingFt > 0
      ? Math.floor(5280 / readoutSpacing.wellSpacingFt)
      : null;
  const inventorySpacingFields = (
    <>
      <div className={styles.spacingItem}>
        <span className={styles.controlLabel}>Unit Well Spacing (ft)</span>
        <span className={styles.controlValue}>
          {spacingText(readoutSpacing?.wellSpacingFt)}
        </span>
      </div>
      {readoutWellsPerMile !== null && (
        <span className={styles.wellsPerMile}>
          {readoutWellsPerMile} wells per mile
        </span>
      )}
      <div className={styles.spacingItem}>
        <span className={styles.controlLabel}>Cross-line Setback (ft)</span>
        <span className={styles.controlValue}>
          {spacingText(readoutSpacing?.crossLineSetbackFt)}
        </span>
      </div>
      <div className={styles.spacingItem}>
        <span className={styles.controlLabel}>In-line Setback (ft)</span>
        <span className={styles.controlValue}>
          {spacingText(readoutSpacing?.inLineSetbackFt)}
        </span>
      </div>
      <div className={styles.spacingItem}>
        <span className={styles.controlLabel}>Spacing Tolerance (ft)</span>
        <span className={styles.controlValue}>
          {spacingText(readoutSpacing?.spacingToleranceFt)}
        </span>
      </div>
    </>
  );

  return (
    <div className={styles.container}>
      {/* Type Curve: unit navigator on its own row above the controls. The
          Inventory Workspace instead folds the navigator into a single wrapping
          controls row (below), matching the mock. */}
      {!chartOnly && !inventoryScope && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {!hideUnitNavigator && <TcUnitNavigator />}
          </div>
        </div>
      )}
      {!chartOnly &&
        (inventoryScope ? (
          // Inventory Workspace: navigator + read-only spacing + verdict/Unit
          // builder all on one row that wraps when there isn't enough width.
          <div className={styles.inventoryControls}>
            {!hideUnitNavigator && <TcUnitNavigator hideLabel borderless />}
            {inventorySpacingFields}
            <div className={styles.inventoryActions}>
              {verdictButtons}
              {unitBuilderButton}
              {expandButton}
            </div>
          </div>
        ) : (
          <div className={styles.controls}>
            <div className={styles.controlsLeft}>
              <label
                htmlFor="tc-gb-target-spacing"
                className={styles.controlLabel}
              >
                Target Spacing (ft)
              </label>
              <input
                id="tc-gb-target-spacing"
                type="number"
                className={styles.spacingInput}
                value={localSpacing ?? ''}
                min={SPACING_LOWER_LIMIT_FT}
                max={SPACING_UPPER_LIMIT_FT}
                step={10}
                readOnly={!workspaceActive}
                disabled={!workspaceActive}
                aria-invalid={spacingError}
                aria-describedby={
                  spacingError ? 'tc-gb-spacing-error' : undefined
                }
                onChange={handleSpacingChange}
                onBlur={(e) => commitSpacing(Number(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitSpacing(Number((e.target as HTMLInputElement).value));
                  }
                }}
              />
              {wellsPerMile !== null && (
                <span className={styles.wellsPerMile}>
                  {wellsPerMile} wells per mile
                </span>
              )}
            </div>
            <div className={styles.controlsRight}>
              <Button
                kind={gbEditMode ? 'primary' : 'tertiary'}
                size="sm"
                renderIcon={Draggable}
                disabled={!workspaceActive || !unitSelected}
                onClick={() => setGbEditMode((v) => !v)}
              >
                {gbEditMode && gbPositionEdits.size > 0
                  ? `Edit Positions (${gbPositionEdits.size})`
                  : 'Edit Positions'}
              </Button>
              {verdictButtons}
              {unitBuilderButton}
            </div>
          </div>
        ))}
      {spacingError && (
        <div
          id="tc-gb-spacing-error"
          className={styles.spacingError}
          role="alert"
        >
          Target Spacing must be between {fmtFt(SPACING_LOWER_LIMIT_FT)} and{' '}
          {fmtFt(SPACING_UPPER_LIMIT_FT)} ft.
        </div>
      )}
      <div className={styles.chartRow}>
        <div className={styles.chartArea}>
          {/* Inventory Workspace empty state: the chart still renders its
              formation bands, with a prompt overlaid until a unit is picked. */}
          {inventoryScope && !unitSelected && !gunBarrelError && (
            <div className={styles.chartEmpty}>Select a unit on the map</div>
          )}
          {gunBarrelError ? (
            <div className={styles.message} role="alert">
              {gunBarrelError}
            </div>
          ) : (
            <GunBarrelChart
              ref={chartHandleRef}
              // Undefined for every other consumer, so the chart keeps its own
              // default height.
              height={chartHeight}
              onSelectionInfoChange={onSelectionInfoChange}
              // Inventory Unit Builder: apply the per-well-type movement rules
              // (LTD held in-formation, permits TVD-only, survey wells locked).
              restrictByWellType={inventoryScope !== null}
              crossFormationPicking={crossFormationPicking}
              requestMode={gunBarrelData?.requestMode}
              subject={gunBarrelData?.subject ?? null}
              formationHorizontals={gunBarrelData?.formationHorizontals ?? null}
              highlightedFormation={chartHighlightedFormation}
              // Inventory Workspace marks the active formation with a blue band
              // border; the Type Curve pages keep the default (chart's own colour).
              highlightColor={inventoryScope ? '#0f62fe' : undefined}
              wells={wells}
              slots={gbSlots}
              // Inventory MAIN page: show the chart's loading view whenever the
              // gun barrel API is in flight — initial load AND every refetch (the
              // section line moved, or a re-slot landed). Elsewhere (plain Type
              // Curve page, Unit Builder) keep the non-flashing behaviour: only
              // overlay before the first data, so a spacing keystroke / boundary
              // edit doesn't blank the chart on each refetch.
              loading={
                inventoryScope && surface === 'main'
                  ? gunBarrelLoading
                  : gunBarrelLoading && !gunBarrelData
              }
              // Drainage sector lines: on the Inventory Workspace use the
              // selected unit's well spacing (shown above the chart), not the
              // type-curve header default. Plain Type Curve pages keep the
              // editable Target Spacing input value.
              spacingFt={
                inventoryScope
                  ? (wellSpacingFt ?? inventoryWellSpacingFt)
                  : localSpacing
              }
              crossLineSetbackFt={
                inventoryScope ? crossLineSetbackFt : undefined
              }
              // Inventory: each formation band draws its own drainage + setback
              // from its zone's config (all formations at once); the scalars above
              // are the fallback when this is empty (plain Type Curve page / draft).
              formationSpacingConfigs={
                inventoryScope ? formationSpacingConfigs : undefined
              }
              unitSelected={unitSelected}
              onSlotClick={workspaceActive ? handleSlotClick : undefined}
              // Inventory (Unit Builder): right-click any marker to value/unvalue
              // it, with the Notes UI in the edit rail. Any well type, FE-only.
              onSlotRightClick={
                inventoryScope ? handleSlotRightClick : undefined
              }
              // The Unit Builder (chartOnly) has no "Edit Positions" toggle — its
              // rail drives editing, so drag/select is always on there.
              editMode={chartOnly || gbEditMode}
              selectedKeys={chartSelectedKeys}
              onSelectedKeysChange={handleChartSelectedKeys}
              showSlotLabels={showSlotLabels}
              onPositionEditsChange={(edits) => {
                setGbPositionEdits(edits);
                onPositionEditsChange?.(edits);
              }}
              filteredUwis={filteredUwis}
              statusMarkers
              // The Unit Builder (chartOnly) drops the image-export trigger.
              exportConfig={
                chartOnly
                  ? undefined
                  : {
                      label: 'Gun Barrel',
                      filenamePrefix: 'type-curve-gun-barrel',
                    }
              }
            />
          )}
        </div>
        {/* The floating Notes panel is the type-curve page's UI; the Unit
            Builder (inventory) uses the rail Notes section instead. */}
        {!inventoryScope && (
          <SlotNotesPanel
            onSaved={handleSlotSaved}
            typeCurveId={typeCurveId.toString()}
            onCancelled={handleSlotCancelled}
          />
        )}
      </div>
    </div>
  );
};

TypeCurveGunBarrel.displayName = 'TypeCurveGunBarrel';
